import React, { useState, useEffect } from "react";
import { NavLink, Link, useLocation, Outlet } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useContent } from "../lib/content";
import LightSource from "./LightSource";
import GoogleTranslate from "./GoogleTranslate";

const LOGO_DEFAULT =
  "https://customer-assets.emergentagent.com/job_inner-pulse-1/artifacts/e5somxk8_61ada848-88a3-4a61-bf38-c4d30a6e58f8.jpg";

const NAV = [
  { to: "/", label: "Home", num: "01" },
  { to: "/about", label: "Bio", num: "02" },
  { to: "/sets", label: "Sets", num: "03" },
  { to: "/videos", label: "Videos", num: "04" },
  { to: "/events", label: "Events", num: "05" },
  { to: "/inner-dance", label: "Festival Pack", shortLabel: "Festival Pack", num: "06" },
  { to: "/releases", label: "Releases", num: "07" },
  { to: "/community", label: "Community", num: "08" },
  { to: "/press-kit", label: "Press Kit", num: "09" },
  { to: "/booking", label: "Booking", num: "10" },
  { to: "/tech-rider", label: "Tech Rider", num: "11" },
  { to: "/reviews", label: "Reviews", num: "12" },
  { to: "/merch", label: "Merch", num: "13" },
];

const FOOTER_NAV = [
  { to: "/about", label: "Bio" },
  { to: "/sets", label: "Sets" },
  { to: "/videos", label: "Videos" },
  { to: "/events", label: "Events" },
  { to: "/merch", label: "Merch" },
  { to: "/press-kit", label: "Press Kit" },
  { to: "/reviews", label: "Reviews" },
  { to: "/booking", label: "Booking" },
  { to: "/tech-rider", label: "Tech Rider" },
];

const SOCIAL_SLOTS = [
  { label: "Soundcloud", slot: "social.soundcloud_url", fallback: "https://soundcloud.com/sandrita-soundation" },
  { label: "Bandcamp", slot: "social.bandcamp_url", fallback: "https://bandcamp.com/" },
  { label: "Youtube", slot: "social.youtube_url", fallback: "https://www.youtube.com/" },
  { label: "Instagram", slot: "social.instagram_url", fallback: "https://www.instagram.com/" },
  { label: "Linktree", slot: "social.linktree_url", fallback: "https://linktr.ee/sandravidalsoundation" },
];

const CONTACT_EMAIL_DEFAULT = "soundationtechnoevents@gmail.com";

export default function Layout() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const LOGO = useContent("brand.logo", LOGO_DEFAULT);
  const CONTACT_EMAIL = useContent("contact.email", CONTACT_EMAIL_DEFAULT);
  const socialItems = SOCIAL_SLOTS.map((s) => ({
    label: s.label,
    url: useContent(s.slot, s.fallback), // eslint-disable-line react-hooks/rules-of-hooks
  }));

  useEffect(() => {
    setOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <div className="relative min-h-screen bg-black text-silver-100" data-testid="layout-root">
      <LightSource />
      {/* Header */}
      <header
        className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-black/95 hairline-b shadow-[0_2px_20px_rgba(0,0,0,0.8)]"
        data-testid="site-header"
      >
        <div className="max-w-[1600px] mx-auto px-5 md:px-10 h-20 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="brand-link">
            <img
              src={LOGO}
              alt="Inner Pulse"
              className="h-11 w-11 object-cover rounded-full"
              style={{ filter: "brightness(1.1) contrast(1.1)" }}
            />
            <span className="font-title text-sm md:text-base font-bold tracking-[0.24em] text-white hidden sm:block">
              INNER&nbsp;PULSE
            </span>
          </Link>

          <div className="flex items-center gap-3 shrink-0">
            <GoogleTranslate />
            <button
              onClick={() => setOpen(!open)}
              className="flex items-center gap-2.5 px-5 py-2.5 border-2 border-white text-white font-title font-extrabold text-sm md:text-base tracking-[0.24em] uppercase hover:bg-white hover:text-black transition-all duration-300"
              data-testid="menu-toggle"
              aria-label="Toggle menu"
              aria-expanded={open}
            >
              {open ? <X size={18} strokeWidth={3} /> : <Menu size={18} strokeWidth={3} />}
              <span>{open ? "Close" : "Menu"}</span>
            </button>
          </div>
        </div>
      </header>

      {/* Left-side drawer menu */}
      {open && (
        <div
          className="fixed inset-0 z-40 bg-black/40 pt-20"
          data-testid="menu-overlay"
          onClick={() => setOpen(false)}
        >
          <nav
            className="h-full w-fit max-w-[92vw] bg-black/98 backdrop-blur-2xl border-r border-white/10 shadow-[8px_0_40px_rgba(0,0,0,0.9)] overflow-y-auto animate-slide-in-left"
            data-testid="vertical-nav"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex flex-col gap-1 md:gap-2 px-6 md:px-10 py-8 md:py-12">
              {NAV.map((n, idx) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.to === "/"}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) => `menu-item ${isActive ? "menu-item-active" : ""}`}
                  data-testid={`menu-item-${n.label.toLowerCase().replace(/ /g, "-")}`}
                  style={{ animationDelay: `${idx * 40}ms` }}
                >
                  <span className="menu-num">/ {n.num}</span>
                  <span className="menu-label">{n.label}</span>
                </NavLink>
              ))}
            </div>
          </nav>
        </div>
      )}

      {/* Main */}
      <main className="relative z-10 page-enter pt-24 lg:pt-28" data-testid="main-content">
        <Outlet />
      </main>

      {/* Footer */}
      <footer
        className="relative z-10 hairline-t mt-32 px-6 md:px-12 pt-16 pb-10"
        data-testid="site-footer"
      >
        <div className="max-w-[1600px] mx-auto grid md:grid-cols-12 gap-10">
          {/* Brand */}
          <div className="md:col-span-5 flex flex-col gap-6" data-testid="footer-brand">
            <img
              src={LOGO}
              alt="Inner Pulse"
              className="w-32 h-32 object-cover rounded-full self-start"
              style={{ filter: "brightness(1.1)" }}
            />
            <div className="flex flex-col gap-3">
              <span className="font-title text-2xl tracking-[0.16em] text-silver-100">
                INNER PULSE
              </span>
              <span className="font-body text-xs text-silver-500 leading-relaxed max-w-xs">
                A ritual translated into rhythm.<br />
                DJ &amp; live techno project — Aotearoa.
              </span>
            </div>
          </div>

          {/* Navigate */}
          <div className="md:col-span-3 flex flex-col gap-4" data-testid="footer-nav">
            <div className="font-title text-[0.6rem] tracking-[0.3em] text-silver-500 uppercase">
              / Navigate
            </div>
            <ul className="flex flex-col gap-2.5">
              {FOOTER_NAV.map((n) => (
                <li key={n.to}>
                  <Link
                    to={n.to}
                    className="font-title text-[0.7rem] tracking-[0.18em] text-silver-300 hover:text-silver-100 transition-colors uppercase"
                    data-testid={`footer-nav-${n.label.toLowerCase().replace(" ", "-")}`}
                  >
                    {n.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Follow */}
          <div className="md:col-span-4 flex flex-col gap-4" data-testid="footer-follow">
            <div className="font-title text-[0.6rem] tracking-[0.3em] text-silver-500 uppercase">
              / Follow
            </div>
            <ul className="flex flex-col gap-2.5">
              {socialItems.map((s) => (
                <li key={s.label}>
                  <a
                    href={s.url}
                    target="_blank"
                    rel="noreferrer"
                    className="font-title text-[0.7rem] tracking-[0.18em] text-silver-300 hover:text-silver-100 transition-colors uppercase"
                    data-testid={`footer-social-${s.label.toLowerCase()}`}
                  >
                    {s.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Contact — full-width horizontal line */}
        <div
          className="hairline-t hairline-b mt-14 py-6 max-w-[1600px] mx-auto flex flex-col md:flex-row items-start md:items-center gap-3 md:gap-6"
          data-testid="footer-contact"
        >
          <span className="font-title text-[0.6rem] tracking-[0.3em] text-silver-500 uppercase shrink-0">
            / Contact
          </span>
          <span className="hidden md:block w-8 h-px bg-silver-700" />
          <a
            href={`mailto:${CONTACT_EMAIL}`}
            className="font-body text-base md:text-lg text-silver-100 hover:text-silver-glow transition-colors tracking-wide break-all"
            data-testid="footer-email"
          >
            {CONTACT_EMAIL}
          </a>
          <span className="hidden md:block flex-1 h-px bg-silver-800" />
          <a
            href={`mailto:${CONTACT_EMAIL}?subject=Booking%20Inquiry%20—%20Inner%20Pulse`}
            className="font-title text-[0.62rem] tracking-[0.28em] uppercase text-silver-300 hover:text-silver-100 transition-colors shrink-0"
            data-testid="footer-contact-cta"
          >
            Send Email →
          </a>
        </div>

        <div className="mt-6 max-w-[1600px] mx-auto flex flex-col md:flex-row justify-between gap-3 text-silver-600">
          <span className="font-title text-[0.6rem] tracking-[0.26em]">
            © {new Date().getFullYear()} INNER PULSE — ALL RIGHTS RESERVED
          </span>
          <span className="font-title text-[0.6rem] tracking-[0.26em] text-silver-500">
            DESIGNED FOR INTERNATIONAL CLUBS &amp; FESTIVALS
          </span>
        </div>
      </footer>
    </div>
  );
}
